
import { Parser } from 'expr-eval';

export class ExpressionEngine {
  private static parser = new Parser();

  /**
   * Safe evaluation of JS expressions using expr-eval library.
   * This provides a sandboxed environment for mathematical and logical expressions.
   */
  static evaluate(expression: string, context: Record<string, any>): any {
    if (!expression) return null;
    
    try {
      // Create a simplified context if needed, or pass directly
      const expr = this.parser.parse(expression);
      return expr.evaluate(context);
    } catch (error) {
      console.warn(`Expression evaluation error for "${expression}":`, error);
      // Fallback for simple string literals that might not parse as math/logic
      if (typeof expression === 'string' && !expression.includes(' ') && !expression.includes('.')) {
          return expression;
      }
      return null;
    }
  }

  /**
   * Executes a block of JavaScript code with the given context.
   * Returns the result of the script execution (the value of the last statement or explicit return).
   */
  static execute(script: string, context: Record<string, any>): any {
    if (!script) return undefined;
    try {
      const keys = Object.keys(context);
      const values = Object.values(context);
      // Execute the script body directly
      const func = new Function(...keys, script);
      return func(...values);
    } catch (error) {
      console.error(`Script execution error:`, error);
      throw error;
    }
  }

  /**
   * Interpolates string templates with ${variable} syntax
   */
  static interpolate(template: string, context: Record<string, any>): string {
    if (!template || typeof template !== 'string') return '';
    
    return template.replace(/\$\{(.*?)\}/g, (match, expr) => {
      try {
        const result = this.evaluate(expr, context);
        return result !== undefined && result !== null ? String(result) : '';
      } catch (e) {
        return '';
      }
    });
  }
  
  /**
   * Interpolates a value, preserving its original type when the template is a single ${expression}.
   * For pure expressions like "${variable}", returns the raw value (including null/undefined).
   * For mixed strings like "Hello ${name}", falls through to string interpolation.
   */
  static interpolateValue(template: string, context: Record<string, any>): any {
    if (template === null || template === undefined) return template;
    if (typeof template !== 'string') return template;

    // If the entire string is a single ${expression}, return the raw value (preserving null/undefined)
    const pureMatch = template.match(/^\$\{(.*?)\}$/);
    if (pureMatch) {
        try {
            return this.evaluate(pureMatch[1], context);
        } catch (e) {
            return null;
        }
    }

    // Otherwise fall through to string interpolation
    return this.interpolate(template, context);
  }

  /**
   * Evaluates a boolean condition
   */
  static evaluateCondition(condition: string, context: Record<string, any>): boolean {
      if (!condition) return true; // Default to true if empty
      // Handle "true"/"false" strings
      if (condition === 'true') return true;
      if (condition === 'false') return false;
      
      const result = this.evaluate(condition, context);
      return !!result;
  }
}
